"use client"

import Image from "next/image";
import {Carousel, CarouselApi, CarouselContent, CarouselItem} from "@/app/components/common/app-carousel/AppCarousel";
import Autoplay from "embla-carousel-autoplay"
import React, {useCallback, useEffect, useState} from "react";
import {CompactPagination} from "@/app/components/common/compact-pagination/CompactPagination";
import {cn, rgbDataURL} from "@/lib/utils";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {useLocale} from "next-intl";

interface IProps {
    onSlideClick?: (index: number) => void;
}

const SLIDES_QUANTITY = 4;

export default function HeroCarousel({onSlideClick}: IProps) {
    const locale = useLocale();
    const [api, setApi] = useState<CarouselApi>()
    const [current, setCurrent] = useState(1)

    const [autoplay] = useState(() =>
        Autoplay({
            delay: 4000,
            stopOnInteraction: false,
        })
    )

    useEffect(() => {
        if (!api) {
            return
        }

        const onSelect = () => {
            const next = api.selectedScrollSnap() + 1
            setCurrent((prev) => prev === next ? prev : next)
        }

        api.on("select", onSelect)

        return () => {
            api.off("select", onSelect)
        }
    }, [api])

    const onSelectNewSlide = (index: number) => {
        if (!api) return
        api.scrollTo(index)
        autoplay.reset()
        setCurrent(index + 1)
    }

    const handleClick = useCallback((i: number) => {
        onSlideClick?.(i)
    }, [onSlideClick])


    return (
        <>
            <div className={'flex mb-[12px] relative group'}>
                <div
                    onClick={() => api?.scrollPrev()}
                    className={'absolute left-[-32px] w-[32px] hidden md:flex md:justify-center md:items-center md:opacity-0 md:group-hover:opacity-100 text-noble-black-300 cursor-pointer top-0 bottom-0 transition-all duration-200 ease-in-out'}>
                    <ChevronLeft/>
                </div>
                <Carousel
                    setApi={setApi}
                    opts={{
                        align: "start",
                        loop: true,
                    }}

                    className="w-full"
                    plugins={[
                        autoplay
                    ]}
                >
                    <CarouselContent>
                        {Array.from({length: SLIDES_QUANTITY}).map((_, index) => (
                            <CarouselItem key={index}
                                          onClick={() => handleClick(index)}
                                          className={cn('cursor-pointer basis-[80%] md:basis-1/3 aspect-video', {'mr-[16px] md:mr-0': index === SLIDES_QUANTITY - 1})}>
                                <Image
                                    blurDataURL={rgbDataURL(32, 36, 40)}
                                    placeholder={'blur'}
                                    fetchPriority={'high'}
                                    width={1568}
                                    height={966}
                                    className="rounded-[8px] w-full h-full"
                                    src={`/banners/${locale}/small/banner-${index + 1}.webp`}
                                    alt={'bonus-banner'}
                                    sizes="
                                      (max-width: 640px) 496px,
                                      (max-width: 768px) 224px,
                                      (max-width: 1024px) 310px,
                                      (max-width: 1280px) 307px,
                                      (max-width: 1536px) 392px,
                                       500px
                                    "
                                    loading={index === 0 ? "eager" : "lazy"}
                                    priority={index === 0}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
                <div
                    onClick={() => api?.scrollNext()}
                    className={'absolute right-[-32px] w-[32px] hidden md:flex md:justify-center md:items-center md:opacity-0 md:group-hover:opacity-100 text-noble-black-300 cursor-pointer top-0 bottom-0 transition-all duration-200 ease-in-out'}>
                    <ChevronRight/>
                </div>
            </div>
            <div className={'flex justify-center md:hidden'}>
                <CompactPagination onSelectNewSlide={onSelectNewSlide} activeIndex={current - 1}
                                   quantity={SLIDES_QUANTITY}/>
            </div>
        </>

    );
}
